import React, { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Input,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckOutlined,
  EyeOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import TableEmptyContent from "../../Components/TableEmptyContent/TableEmptyContent";
import { useNotifications } from "../../contexts/NotificationContext";
import { mapNotificationRow, THRESHOLD_EVENT_TYPE } from "../../utils/notifications";
import ThresholdConfigModal from "../../Components/Notifications/ThresholdConfigModal";
import NotificationDetailModal from "../../Components/Notifications/NotificationDetailModal";
import "../../styles/Notifications/Notifications.css";

const { Text, Title } = Typography;

export default function NotificationsPage() {
  const navigate = useNavigate();
  const {
    unreadCount,
    subscribe,
    fetchList,
    markRead,
    markUnread,
    markAllRead,
  } = useNotifications();

  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [filterType, setFilterType] = useState("all"); // 'all' | 'unread' | 'threshold'
  const [selectedNotif, setSelectedNotif] = useState(null);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [allTotal, setAllTotal] = useState(0);
  const [thresholdTotal, setThresholdTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const markingAllRef = useRef(false);

  const handleMarkAllRead = useCallback(async () => {
    if (markingAllRef.current) return;
    markingAllRef.current = true;
    setMarkingAll(true);
    try {
      await markAllRead();
    } catch (err) {
      markingAllRef.current = false;
      setMarkingAll(false);
    }
  }, [markAllRead]);

  // Stay disabled after success; re-enable once unread count reaches 0
  // so a later batch of new notifications can be marked again.
  useEffect(() => {
    if (unreadCount === 0) {
      markingAllRef.current = false;
      setMarkingAll(false);
    }
  }, [unreadCount]);
  const searchTimerRef = useRef(null);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchList({
        page,
        page_size: pageSize,
        unread_only: filterType === "unread",
        ...(filterType === "threshold" ? { event_type: THRESHOLD_EVENT_TYPE } : {}),
        search: searchText,
      });
      if (res?.data?.status) {
        setItems((res.data.data || []).map(mapNotificationRow));
        setTotal(res.data.total || 0);
      }
    } finally {
      setLoading(false);
    }
  }, [fetchList, page, pageSize, filterType, searchText]);

  const refreshAllTotal = useCallback(async () => {
    const [allRes, thresholdRes] = await Promise.all([
      fetchList({ page: 1, page_size: 1 }),
      fetchList({ page: 1, page_size: 1, event_type: THRESHOLD_EVENT_TYPE }),
    ]);
    if (allRes?.data?.status) setAllTotal(allRes.data.total || 0);
    if (thresholdRes?.data?.status) setThresholdTotal(thresholdRes.data.total || 0);
  }, [fetchList]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    refreshAllTotal();
  }, [refreshAllTotal]);

  useEffect(() => {
    return subscribe(() => {
      refreshAllTotal();
      loadNotifications();
    });
  }, [subscribe, refreshAllTotal, loadNotifications]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setPage(1);
      setSearchText(value);
    }, 350);
  };

  const handleTabChange = (tab) => {
    setFilterType(tab);
    setPage(1);
  };

  const handleMarkRead = (id) => markRead([id]);
  const handleToggleRead = (row) => (row.read ? markUnread([row.id]) : markRead([row.id]));

  const columns = [
    {
      title: "Status",
      dataIndex: "read",
      key: "read",
      width: 90,
      align: "center",
      render: (read) =>
        read ? (
          <Tag color="default">Read</Tag>
        ) : (
          <Tag
            color="processing"
            icon={
              <span
                style={{
                  display: "inline-block",
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "#1890ff",
                  marginRight: 4,
                }}
              />
            }
          >
            New
          </Tag>
        ),
    },
    {
      title: "Spec & Card No",
      key: "specCard",
      width: 170,
      render: (_, row) => (
        <div>
          <Text strong style={{ display: "block", color: "#1e3a8a" }}>
            {row.spec}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Card: {row.cardNo}
          </Text>
        </div>
      ),
    },
    {
      title: "Event",
      dataIndex: "message",
      key: "message",
      width: 300,
      render: (msg) => (
        <Tooltip title={msg}>
          <Text
            style={{
              fontSize: 13,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {msg}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: "Defect vs Threshold",
      key: "thresholdComparison",
      width: 220,
      align: "center",
      render: (_, row) =>
        row.isThreshold ? (
          <div>
            <span className="notif_threshold_pill">
              Defects: <strong>{row.defectCount}</strong>
            </span>
            <div style={{ fontSize: 11, color: "#dc2626", marginTop: 2, fontWeight: 600 }}>
              Rate {row.currentRate} vs baseline {row.baselineRate} (+{row.thresholdPct}%)
            </div>
          </div>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            —
          </Text>
        ),
    },
    {
      title: "Timestamp",
      dataIndex: "timestamp",
      key: "timestamp",
      width: 140,
      render: (t) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t}
        </Text>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      align: "center",
      render: (_, row) => (
        <Space size="small">
          <Tooltip title="View Alert Details">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined style={{ color: "#2563eb" }} />}
              onClick={() => {
                if (!row.read) handleMarkRead(row.id);
                setSelectedNotif(row);
              }}
            />
          </Tooltip>
          <Tooltip title={row.read ? "Mark as unread" : "Mark as read"}>
            <Button
              type="text"
              size="small"
              icon={<CheckOutlined style={{ color: row.read ? "#94a3b8" : "#16a34a" }} />}
              onClick={() => handleToggleRead(row)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="container_div">
      <Helmet>
        <title>SpecOptimizer | Closed-Loop Notifications</title>
      </Helmet>

      {/* Page Card */}
      <div className="box_wraper_div" style={{ padding: "20px 24px" }}>
        {/* Header Bar */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 14,
            marginBottom: 20,
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Button icon={<ArrowLeftOutlined />} size="small" onClick={() => navigate(-1)}>
                Back
              </Button>
              <Title level={3} style={{ margin: 0, color: "#1e1b4b" }}>
                Notifications
              </Title>
              {unreadCount > 0 && (
                <Tag color="red" style={{ fontWeight: 600 }}>
                  {unreadCount} Unread
                </Tag>
              )}
            </div>
          </div>

          <Space wrap>
            {unreadCount > 0 && (
              <Button
                onClick={handleMarkAllRead}
                disabled={markingAll}
              >
                Mark All as Read
              </Button>
            )}
          </Space>
        </div>

        {/* Search & Filter Toolbar */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
            marginBottom: 16,
            background: "#f8fafc",
            padding: "10px 14px",
            borderRadius: 8,
            border: "1px solid #e2e8f0",
          }}
        >
          <Space wrap>
            <Button
              type={filterType === "all" ? "primary" : "default"}
              size="small"
              onClick={() => handleTabChange("all")}
            >
              All Alerts ({allTotal})
            </Button>
            <Button
              type={filterType === "unread" ? "primary" : "default"}
              size="small"
              onClick={() => handleTabChange("unread")}
            >
              Unread ({unreadCount})
            </Button>
            <Button
              type={filterType === "threshold" ? "primary" : "default"}
              size="small"
              onClick={() => handleTabChange("threshold")}
            >
              Defect Count Alerts ({thresholdTotal})
            </Button>
          </Space>

          <Input
            placeholder="Search spec, card no..."
            prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
            value={searchInput}
            onChange={handleSearchChange}
            allowClear
            style={{ width: 280 }}
          />
        </div>

        {/* Full Table */}
        <Table
          rowKey="id"
          className="antdTable"
          columns={columns}
          dataSource={items}
          loading={loading}
          scroll={{ x: "max-content" }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "50"],
            showTotal: (t, range) => `Showing ${range[0]} to ${range[1]} of ${t} alerts`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
          locale={{ emptyText: <TableEmptyContent /> }}
        />
      </div>

      <ThresholdConfigModal open={isConfigOpen} onClose={() => setIsConfigOpen(false)} />

      <NotificationDetailModal
        open={Boolean(selectedNotif)}
        notification={selectedNotif}
        onClose={() => setSelectedNotif(null)}
      />
    </div>
  );
}
